import React from 'react';
import { Seat, SeatMealPlanResult } from '../../interfaces/mealPlan';
import { getMealPeriodLabel, getMealPlanLabel } from '../../interfaces/mealPlan';
import { formatPrice } from '../../utils/formatPrice';

interface AllowanceTrackerProps {
    seat: Seat;
    result: SeatMealPlanResult | null;
}

const AllowanceTracker: React.FC<AllowanceTrackerProps> = ({ seat, result }) => {
    if (seat.type !== 'hotel_guest') return null;
    if (!result && !seat.rateName) return null;

    return (
        <div style={{
            padding: '8px 12px', backgroundColor: '#FAFBFF',
            borderBottom: '1px solid #EAECF0', fontSize: 12,
        }}>
            {/* Header */}
            <div className='d-flex justify-content-between align-items-center'>
                <span style={{ fontWeight: 600, color: '#344054' }}>
                    {result?.currentMealPeriod ? getMealPeriodLabel(result.currentMealPeriod) : 'No Active Meal Period'}
                    {' '}Allowance
                    <span style={{ color: '#667085', fontWeight: 400 }}>
                        {' '} &middot; Room {seat.roomNumber} - {seat.guestName}
                    </span>
                </span>
                <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    backgroundColor: '#ECFDF3',
                    color: '#027A48',
                }}>
                    {getMealPlanLabel(seat.rateName)}
                </span>
            </div>

            {/* Warnings */}
            {result?.warnings && result.warnings.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    {result.warnings.map((w, i) => (
                        <div key={i} style={{
                            padding: '4px 8px', borderRadius: 4,
                            backgroundColor: '#FFF6ED', color: '#B93815',
                            fontSize: 11, marginBottom: 3, display: 'flex',
                            alignItems: 'center', gap: 6,
                        }}>
                            <span style={{ fontSize: 14 }}>&#9888;</span>
                            {w}
                        </div>
                    ))}
                </div>
            )}

            {/* Allowance bars */}
            {result?.allowanceSummary && result.allowanceSummary.length > 0 && (
                <div className='d-flex gap-3 mt-1' style={{ flexWrap: 'wrap' }}>
                    {result.allowanceSummary.map((item, i) => {
                        const pct = item.max > 0 ? Math.min((item.used / item.max) * 100, 100) : 0;
                        const isFull = item.used >= item.max;
                        return (
                            <div key={i} style={{ minWidth: 120 }}>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <span style={{ fontSize: 11, color: '#475467' }}>{item.label}</span>
                                    <span style={{
                                        fontSize: 11, fontWeight: 600,
                                        color: isFull ? '#B93815' : '#027A48',
                                    }}>
                                        {item.used}/{item.max}
                                    </span>
                                </div>
                                <div style={{
                                    height: 4, borderRadius: 2, backgroundColor: '#E4E7EC',
                                    marginTop: 2, overflow: 'hidden',
                                }}>
                                    <div style={{
                                        height: '100%', borderRadius: 2,
                                        width: `${pct}%`,
                                        backgroundColor: isFull ? '#F79009' : '#12B76A',
                                        transition: 'width 0.3s ease',
                                    }} />
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ minWidth: 100 }}>
                        <span style={{ fontSize: 11, color: '#98A2B3' }}>Drinks: not included</span>
                    </div>
                </div>
            )}

            {/* No active meal period */}
            {!result?.currentMealPeriod && !result?.isWithinTimeWindow && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#B93815' }}>
                    Outside meal period. All items will be charged as extra.
                </div>
            )}

            {/* Already used */}
            {result?.isAllowanceAlreadyUsed && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#B93815', fontWeight: 600 }}>
                    Allowance already used for this meal period. All items charged as extra.
                </div>
            )}

            {/* Totals */}
            {result && (result.totalIncluded > 0 || result.totalExtra > 0) && (
                <div className='d-flex gap-3 mt-1' style={{ fontSize: 11 }}>
                    {result.totalIncluded > 0 && (
                        <span style={{ color: '#027A48' }}>
                            Included: {formatPrice(result.totalIncluded)}
                        </span>
                    )}
                    {result.totalExtra > 0 && (
                        <span style={{ color: '#B93815' }}>
                            Extra: {formatPrice(result.totalExtra)}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default AllowanceTracker;
